// Inverse of filterStateToV3Filter: maps a BE V3 HpWebListingFilterDto back to the
// FE FilterState — used to seed the SRP filter UI from the BE-resolved slug defaults.
import type { PartialDeep } from 'type-fest';
import type { HpWebListingFilterDto } from '@zg-rentals/ts-hotpads-api-client';
import { HpSearchFlag } from '@zg-rentals/ts-hotpads-api-client';
import {
  type AdditionalOptType,
  type AmenityType,
  type CreatedWithinType,
  DEFAULT_FILTERS,
  type FilterState,
  type LaundryType,
  type PetType,
  type PropertyType,
  type RentalType,
  type RestrictionType,
  VALID_PROPERTY_TYPE_VALUES,
  VALID_RENTAL_TYPE_VALUES,
} from './filterTypes';
import { normalizeMoveInDate } from './filterValidation';

// Partial because the wire omits unset fields.
type WebListingFilter = PartialDeep<HpWebListingFilterDto> & { moveInDate?: string };

const FLAG_TO_PROPERTY_TYPE: Partial<Record<HpSearchFlag, PropertyType>> = {
  apartment: 'apartment',
  condo: 'condo',
  duplex: 'duplex',
  house: 'house',
  townhouse: 'townhouse',
};
const FLAG_TO_RENTAL_TYPE: Partial<Record<HpSearchFlag, RentalType>> = {
  corporateHousing: 'corporate',
  standardRental: 'rental',
  roomForRent: 'room',
  sublet: 'sublet',
};
const FLAG_TO_PET: Partial<Record<HpSearchFlag, PetType>> = { allowsCats: 'cats', allowsDogs: 'dogs' };
const FLAG_TO_LAUNDRY: Partial<Record<HpSearchFlag, LaundryType>> = {
  laundryInUnit: 'inUnit',
  laundryShared: 'shared',
};
const FLAG_TO_AMENITY: Partial<Record<HpSearchFlag, AmenityType>> = {
  airConditioning: 'cooling',
  heating: 'heating',
  parking: 'parking',
  gatedEntry: 'gatedEntry',
  doorman: 'doorman',
  fitnessCenter: 'fitnessCenter',
  pool: 'swimmingPool',
  dishwasher: 'dishwasher',
};
const FLAG_TO_RESTRICTION: Partial<Record<HpSearchFlag, RestrictionType>> = {
  incomeRestricted: 'income',
  seniorHousing: 'senior',
  studentHousing: 'student',
  militaryHousing: 'military',
};
const FLAG_TO_ADDITIONAL_OPT: Partial<Record<HpSearchFlag, AdditionalOptType>> = {
  listedByOwner: 'forRentByOwner',
  acceptingApplications: 'acceptingApplications',
  photos: 'requiresPhotos',
  price: 'requiresPrice',
  specialOffers: 'hasOffers',
};
// Inverse of ORDER_BY_TO_V3 in filterStateToV3.ts.
const V3_ORDER_BY_TO_FE: Record<string, string> = {
  priceAsc: 'lowPrice',
  priceDesc: 'highPrice',
  timeActivatedDesc: 'activated',
  viewsDesc: 'weekViews',
  scoreDesc: 'score',
};
const HOUR_MS = 60 * 60 * 1000;
const MS_TO_CREATED_WITHIN: Record<number, CreatedWithinType> = {
  [HOUR_MS]: 'hour',
  [24 * HOUR_MS]: 'day',
  [168 * HOUR_MS]: 'week',
  [720 * HOUR_MS]: 'month',
};

const orNull = (v: number | null | undefined): number | null => v ?? null;

export function slugDefaultsToFilterState(filter: WebListingFilter | null | undefined): FilterState {
  if (!filter) return { ...DEFAULT_FILTERS };

  const propertyTypes: Array<PropertyType> = [];
  const rentalTypes: Array<RentalType> = [];
  const pets: Array<PetType> = [];
  const laundry: Array<LaundryType> = [];
  const amenities: Array<AmenityType> = [];
  const additionalOpts: Array<AdditionalOptType> = [];
  let restrictions: RestrictionType | null = DEFAULT_FILTERS.restrictions;
  let furnished = false;

  for (const flag of filter.mustHave ?? []) {
    if (FLAG_TO_PROPERTY_TYPE[flag]) propertyTypes.push(FLAG_TO_PROPERTY_TYPE[flag] as PropertyType);
    else if (FLAG_TO_RENTAL_TYPE[flag]) rentalTypes.push(FLAG_TO_RENTAL_TYPE[flag] as RentalType);
    else if (FLAG_TO_PET[flag]) pets.push(FLAG_TO_PET[flag] as PetType);
    else if (FLAG_TO_LAUNDRY[flag]) laundry.push(FLAG_TO_LAUNDRY[flag] as LaundryType);
    // Broad `laundry` has no single FE equivalent; it ≡ in-unit + shared (legacy
    // `apartments-for-rent-with-laundry` = `laundry=inUnit-shared`).
    else if (flag === HpSearchFlag.LAUNDRY) laundry.push('inUnit', 'shared');
    else if (FLAG_TO_AMENITY[flag]) amenities.push(FLAG_TO_AMENITY[flag] as AmenityType);
    else if (FLAG_TO_RESTRICTION[flag]) restrictions = FLAG_TO_RESTRICTION[flag] as RestrictionType;
    else if (FLAG_TO_ADDITIONAL_OPT[flag]) additionalOpts.push(FLAG_TO_ADDITIONAL_OPT[flag] as AdditionalOptType);
    else if (flag === HpSearchFlag.FURNISHED) furnished = true;
  }

  const bedsMin = orNull(filter.beds?.min);
  const bedsMax = orNull(filter.beds?.max);
  const activatedMs = filter.activatedWithin == null ? null : Number(filter.activatedWithin);

  return {
    ...DEFAULT_FILTERS,
    // all types selected ≡ no filter → [] (canonical, mirroring deserializeFromUrl)
    propertyTypes: new Set(propertyTypes).size === VALID_PROPERTY_TYPE_VALUES.size ? [] : propertyTypes,
    rentalTypes: new Set(rentalTypes).size === VALID_RENTAL_TYPE_VALUES.size ? [] : rentalTypes,
    pets,
    // Dedupe in case a slug default carries broad `laundry` alongside a specific flag.
    laundry: Array.from(new Set(laundry)),
    amenities,
    additionalOpts,
    restrictions,
    furnished,
    price: { min: orNull(filter.price?.min), max: orNull(filter.price?.max) },
    bedrooms: { min: bedsMin, max: bedsMax },
    bathrooms: { min: orNull(filter.baths?.min) },
    sqft: { min: orNull(filter.sqft?.min), max: orNull(filter.sqft?.max) },
    keywords: filter.keywords ?? '',
    orderBy: filter.orderBy ? (V3_ORDER_BY_TO_FE[filter.orderBy] ?? null) : null,
    createdWithin: activatedMs !== null ? (MS_TO_CREATED_WITHIN[activatedMs] ?? null) : null,
    moveInDate: normalizeMoveInDate(null, filter.moveInDate ?? null),
  };
}
